import { fromSpecific, scope, TypedNode, UnknownSchema } from '@rehaje/core'
import { reactive, untrack } from '@rehaje/reactive'
import { isArray, toArr, toReadonly, traverseTo } from '@rehaje/shared'

import { defaultLocales } from './constants'

import type { MappingFunctionPlugin } from './plugin'
import type {
  AnyTypedNode,
  FunctionSpecific,
  SchemaFunctionSpecific,
  TypedSchema,
  TypedSpecific,
  TypedSpecificLike
} from '@rehaje/core'
import type { RecursionType } from '@rehaje/shared'

export interface MappingFunctionSpecific extends TypedSpecificLike {
  return?: RecursionType<TypedSpecific>
  type: 'MappingFunction'
}

@scope({
  type: 'Block',
  variables() {
    return [
      {
        get() {
          return this.getSelfViewScope()?.getLocalVariable(this.itemName)
        },
        group: 'MappingFunction',
        name: this.itemName,
        schema() {
          return this.getItemSchema()
        },
        title: this.locales.item,
        type: 'Local'
      },
      {
        get() {
          return this.getSelfViewScope()?.getLocalVariable(this.indexName)
        },
        group: 'MappingFunction',
        name: this.indexName,
        schema: {
          type: 'Schema.Number'
        },
        title: this.locales.index,
        type: 'Local'
      },
      {
        get() {
          const parentVariables = this.getParent()?.getViewScope().variables
          if (parentVariables) {
            return {
              [this.indexName]: parentVariables[this.indexName],
              [this.itemName]: parentVariables[this.itemName],
              [this.rollupName]: parentVariables[this.rollupName]
            }
          }
        },
        group: 'MappingFunction',
        name: this.rollupName,
        schema() {
          return this.getRollupSchema()
        },
        title: this.locales.rollup,
        type: 'Local'
      }
    ]
  }
})
export class MappingFunctionNode extends TypedNode {
  __r_cache_versions: number[] = []

  @fromSpecific()
  @reactive('deep')
  return?: RecursionType<AnyTypedNode>

  /**
   * 直属子节点都是主节点，那么该节点就是纯实体节点，可以开启缓存
   */
  @reactive('computed')
  get __r_is_pure_entity() {
    return this.__r_children.every((child) => child.isEntityNode())
  }

  get indexName(): string {
    return this.plugin.props.indexName as string
  }

  get itemName(): string {
    return this.plugin.props.itemName as string
  }

  get locales(): typeof defaultLocales {
    return (this.plugin.props.locales ??
      defaultLocales) as typeof defaultLocales
  }

  get plugin(): MappingFunctionPlugin {
    return this.getPlugin()
  }

  get rollupName(): string {
    return this.plugin.props.rollupName as string
  }

  __r_createWithCache(transpile: (item: any, i: number | number[]) => any) {
    const items: any[] = []
    const results: any[] = []
    const versions: number[] = []
    const cacheVersions = this.__r_cache_versions

    return (item: any, i: number, primaryKey?: PropertyKey) => {
      const realItem = typeof item === 'function' ? item() : item
      if (
        versions[i] === cacheVersions[i] &&
        i in items &&
        (primaryKey === undefined
          ? items[i] === realItem
          : items[i]?.[primaryKey] === realItem?.[primaryKey])
      ) {
        return results[i]
      }
      const result = transpile(item, i)
      items[i] = realItem
      results[i] = result
      cacheVersions[i] ??= 0
      cacheVersions[i]++
      versions[i] = cacheVersions[i]
      return result
    }
  }

  getItemSchema(): TypedSchema {
    const itemParameterSchema = this.getParameterSchema(this.itemName)
    const mappingReturn = this.takeReturn()
    const returnNode = mappingReturn instanceof TypedNode ? mappingReturn : this
    const item = returnNode.getViewScope()?.variables[this.itemName] as any
    return this.__r_store.definition.inferWithSchema(item, itemParameterSchema)
  }

  getParameterSchema(name: string): TypedSchema {
    const schema = this.getEntityParent()?.getTypedSchema(
      this.getEntityBelongs()
    ) as FunctionSpecific | SchemaFunctionSpecific | undefined
    return (
      toArr(schema?.parameters).find((schema) => schema.name === name) ??
      UnknownSchema
    )
  }

  getRollupSchema(): TypedSchema {
    const rollupParameterSchema = this.getParameterSchema(this.rollupName)
    const rollup = this.getViewScope()?.variables[this.rollupName] as any
    return this.__r_store.definition.inferWithSchema(
      rollup,
      rollupParameterSchema
    )
  }

  refreshCache(start = 0) {
    for (let i = start; i < this.__r_cache_versions.length; i++) {
      this.__r_cache_versions[i] ??= 0
      this.__r_cache_versions[i]++
    }
  }

  takeReturn() {
    if (isArray(this.return)) return this.return[0]
    return this.return
  }

  override transpile() {
    const transpile = (item: any, i: number | number[]) => {
      const variables = {
        get [this.indexName]() {
          return isArray(i) ? i.at(-1) : i
        },
        get [this.itemName]() {
          return toReadonly(typeof item === 'function' ? item() : item)
        }
      }
      const mappingReturn = untrack(() => this.fork(i, variables).return)
      return this.batchEvaluate(() => {
        return traverseTo(mappingReturn, (target) => {
          if (target instanceof TypedNode) {
            if (target.isEntityNode()) {
              return untrack(() => target.evaluate())
            }
            return target.evaluate()
          }
          return target
        })
      })
    }

    if (!this.__r_is_pure_entity) return transpile

    return this.__r_createWithCache(transpile)
  }
}

declare global {
  export interface RehajeSpecifics {
    MappingFunction: MappingFunctionSpecific
  }
  export interface RehajeTypedNodes {
    MappingFunction: MappingFunctionNode
  }
}
